'use client'
import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import { PaginationProps } from './Pagination.interface'
import { getPaginationDisplayPages } from './getPaginationDisplayPages'
import styles from './Pagination.module.scss'
import IconAngleRightRed from '../../../../../public/assets/images/icon_angle_right_red.svg'

export const PaginationControls = (props: PaginationProps) => {
  const { totalPages, currentPage, setCurrentPage } = props

  // Define button limits for different screen sizes
  const MOBILE_MAX = 7 // minimum for mobile to show < 1 ... 4 5 6 ... 14 >
  const TABLET_MIN = 10

  // dynamic resizing of pagination
  const [maxVisibleButtons, setMaxVisibleButtons] = useState(MOBILE_MAX)
  useEffect(() => {
    const updateMaxButtons = () => {
      if (window.innerWidth >= 768) {
        setMaxVisibleButtons(TABLET_MIN)
      } else {
        setMaxVisibleButtons(MOBILE_MAX)
      }
    }

    // Initialize value and set up resize listener
    updateMaxButtons()
    window.addEventListener('resize', updateMaxButtons)

    return () => window.removeEventListener('resize', updateMaxButtons)
  }, [])

  if (totalPages <= 1) return null

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
      // Scroll to top of results when page changes (handlePageChange passed from parent component)
      document.getElementById('report-grid-container')?.scrollIntoView({ behavior: 'smooth' })
      // Fallback for full page scroll if ID is missing or component is used elsewhere
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handlePageKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>, page: number) => {
    if (e.key !== 'Enter' && e.key !== ' ') {
      return
    }
    e.preventDefault()
    handlePageChange(page)
  }

  const displayPages = getPaginationDisplayPages({
    totalPages,
    currentPage,
    maxVisibleButtons,
  })

  return (
    <div className={styles.pagination}>
      {/* Previous Button */}
      <button
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={styles.pageButton}
      >
        <Image
          src={IconAngleRightRed.src}
          alt="Previous Result Page"
          width={6}
          height={11}
          className={`inline-block! align-middle ${styles.prevPage}`}
        />
      </button>

      {/* Page Numbers */}
      {currentPage > 0 &&
        displayPages.map((page, index) =>
          page === '...' ? (
            <span
              key={`ellipsis-${index}`}
              className={styles.ellipsis}
              aria-hidden="true"
            >
              ...
            </span>
          ) : (
            <span
              key={page}
              role="button"
              tabIndex={0}
              aria-label={`Go to page ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
              onClick={() => handlePageChange(page as number)}
              onKeyDown={(e) => handlePageKeyDown(e, page as number)}
              className={page === currentPage ? styles.activePage : styles.pageNumber}
            >
              {page}
            </span>
          ),
        )}

      {/* Next Button */}
      <button
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={styles.pageButton}
      >
        <Image
          src={IconAngleRightRed.src}
          alt="Next Result Page"
          width={6}
          height={11}
          className={`inline-block! align-middle ${styles.nextPage}`}
        />
      </button>
    </div>
  )
}
